import { defaultShouldDehydrateQuery, QueryClient } from '@tanstack/react-query'
import { type Persister, persistQueryClient } from '@tanstack/react-query-persist-client'
import { openDB } from 'idb'
import { env } from './env.ts'

declare module '@tanstack/react-query' {
	interface Register {
		queryMeta: {
			/**
			 * `false` keeps the query out of the persisted cache below. For data that cannot survive
			 * a structured clone — a closure, a class instance whose methods a consumer calls — where
			 * the whole client is persisted as one blob, so one unclonable value loses every query's
			 * persistence, not just its own.
			 */
			persist?: boolean
		}
	}
}

export const queryClient = new QueryClient({
	defaultOptions: { queries: { gcTime: 1000 * 60 * 60 * 24, retry: false, retryOnMount: false } },
})

if (env.ENABLE_QUERY_CACHE) {
	const createIDBPersister = () => {
		const db = openDB('reactQuery', 1, { upgrade: (db) => db.createObjectStore('queryCache') })
		return {
			persistClient: async (client) => {
				try {
					await (await db).put('queryCache', client, 'queryCache')
				} catch (error) {
					// A value the structured clone cannot carry rejects the whole blob. Caught so it
					// surfaces as an error rather than an unhandled rejection that silently leaves the
					// cache never written — see the `persist` meta flag above for the way out
					console.error('Failed to persist the query cache', error)
				}
			},
			restoreClient: async () => (await db).get('queryCache', 'queryCache'),
			removeClient: async () => void (await db).delete('queryCache', 'queryCache'),
		} as Persister
	}
	await Promise.all(
		persistQueryClient({
			queryClient,
			persister: createIDBPersister(),
			dehydrateOptions: {
				shouldDehydrateQuery(query) {
					if (query.meta?.persist === false) return false
					// Socket-fed caches: restoring one would replay stale stream state as if it were live
					if (query.queryKey.some((key) => typeof key === 'string' && key.includes('STREAM'))) return false

					return defaultShouldDehydrateQuery(query)
				},
			},
		}),
	)
}
